import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/components/ui/use-toast'
import * as queuesApi from '../api/queues'
import type { Queue } from '../types'

/**
 * Fetch all queues, optionally filtered by profile
 */
export function useQueues(profileId?: string) {
    return useQuery({
        queryKey: ['social-queues', profileId],
        queryFn: () => queuesApi.getQueues(profileId)
    })
}

/**
 * Create a new queue
 */
export function useCreateQueue() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: (queue: Partial<Queue>) => queuesApi.createQueue(queue),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['social-queues'] })
            toast({
                title: "✅ Queue created",
                description: "Your new queue is ready to use.",
                duration: 3000,
            })
        },
        onError: (error: any) => {
            toast({
                title: "❌ Failed to create queue",
                description: error.message || "Unable to create queue. Please try again.",
                variant: "destructive",
                duration: 0,
            })
        }
    })
}

/**
 * Update an existing queue
 */
export function useUpdateQueue() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: ({ id, ...updates }: Partial<Queue> & { id: string }) =>
            queuesApi.updateQueue(id, updates),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['social-queues'] })
            toast({
                title: "✅ Queue updated",
                description: "Changes saved successfully.",
                duration: 3000,
            })
        },
        onError: (error: any) => {
            toast({
                title: "❌ Failed to update queue",
                description: error.message || "Unable to save changes. Please try again.",
                variant: "destructive",
                duration: 0,
            })
        }
    })
}

/**
 * Delete a queue
 */
export function useDeleteQueue() {
    const queryClient = useQueryClient()
    const { toast } = useToast()

    return useMutation({
        mutationFn: (id: string) => queuesApi.deleteQueue(id),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: ['social-queues'] })
            toast({
                title: "✅ Queue deleted",
                description: "The queue has been removed.",
                duration: 3000,
            })
        },
        onError: (error: any) => {
            toast({
                title: "❌ Failed to delete queue",
                description: error.message || "Unable to delete queue. Please try again.",
                variant: "destructive",
                duration: 0,
            })
        }
    })
}
